import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loader2, ArrowLeft, LogOut, StickyNote, Send, Mail, Phone, Calendar, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { ADMIN_TOKEN_KEY, adminAuthHeader, getAdminToken } from "@/hooks/useAdminAuth";
import { extractApiError } from "@/utils/apiError";
import { EVENT_GROUP_COLOR, eventDescriptor, formatEvent } from "@/utils/eventLabels";
import CopyPasswordLinkButton from "@/components/admin/CopyPasswordLinkButton";
import AutoLoginLinkButton from "@/components/admin/AutoLoginLinkButton";
import GrantFullAccessButton from "@/components/admin/GrantFullAccessButton";
import GrantBundle234Button from "@/components/admin/GrantBundle234Button";
import PurchaseMarkersPanel from "@/components/admin/PurchaseMarkersPanel";
import GenerateUnlockLinkButton from "@/components/admin/GenerateUnlockLinkButton";
import GeneratePurchaseLinkButton from "@/components/admin/GeneratePurchaseLinkButton";
import TrackedLinksPanel from "@/components/admin/TrackedLinksPanel";
import UserScheduledMessagesPanel from "@/components/admin/UserScheduledMessagesPanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmt = (iso) => { try { return new Date(iso).toLocaleString("it-IT"); } catch { return iso; } };

export default function AdminUserDetailPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchData = () => axios.get(`${API}/admin/users/${userId}`, { headers: adminAuthHeader() }).then(r => setData(r.data));

  useEffect(() => {
    if (!getAdminToken()) { navigate("/admin/dashboard"); return; }
    fetchData().catch(() => navigate("/admin/dashboard")).finally(() => setLoading(false));
  }, [userId, navigate]);

  const addNote = async () => {
    const text = newNote.trim();
    if (!text) return; // Button is already disabled while empty/saving.
    setSaving(true);
    try {
      await axios.post(`${API}/admin/users/${userId}/notes`, { note: text }, { headers: adminAuthHeader() });
      setNewNote("");
      await fetchData();
      toast.success("Nota salvata");
    } catch (err) {
      const status = err?.response?.status;
      const detail = extractApiError(err, "Errore sconosciuto");
      if (status === 401) {
        toast.error("Sessione scaduta. Effettua di nuovo l'accesso.");
        sessionStorage.removeItem(ADMIN_TOKEN_KEY);
        navigate("/admin/dashboard");
      } else {
        toast.error(`Impossibile salvare la nota: ${detail}`);
      }
      console.error("[addNote]", err);
    } finally { setSaving(false); }
  };

  if (loading || !data) return <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--np-tertiary)" }}><Loader2 className="animate-spin" size={26} style={{ color: "var(--np-secondary)" }} /></div>;

  const { user, events, notes } = data;

  return (
    <main className="min-h-screen pb-20" style={{ background: "var(--np-tertiary)" }} data-testid="admin-user-detail">
      <header style={{ background: "var(--np-primary)" }}>
        <div className="mx-auto px-6 md:px-10 py-3 flex items-center justify-between" style={{ maxWidth: 1400 }}>
          <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-sm font-semibold text-white"><ArrowLeft size={15} /> Dashboard</Link>
          <h1 className="text-lg font-bold text-white" style={{ fontFamily: "Merriweather, serif" }}>Dettaglio utente</h1>
          <button onClick={() => { sessionStorage.removeItem(ADMIN_TOKEN_KEY); navigate("/"); }} className="text-xs px-3 py-2 rounded-full border border-white/15 text-white hover:bg-white/10"><LogOut size={13} /> Esci</button>
        </div>
      </header>

      <section className="mx-auto px-6 md:px-10 pt-8" style={{ maxWidth: 1400 }}>
        <div className="bg-white rounded-2xl border p-6 mb-6" style={{ borderColor: "rgba(26,42,58,0.08)" }}>
          <h2 className="text-xl md:text-2xl mb-3" style={{ fontFamily: "Merriweather, serif", color: "var(--np-primary)" }}>{user.name}</h2>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm" style={{ color: "var(--np-muted)" }}>
            <span className="inline-flex items-center gap-1.5"><Mail size={13} /> {user.email}</span>
            <span className="inline-flex items-center gap-1.5"><Phone size={13} /> {user.phone}</span>
            {user.phone_secondary && (
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md" style={{ background: "#fef3c7", color: "#92400e" }} title={`Secondario aggiunto via richiesta L.2 il ${user.phone_secondary_at || ""}`}>
                <Phone size={13} /> +{user.phone_secondary} <span className="text-[10px] uppercase tracking-wide">(secondario · usato per WA)</span>
              </span>
            )}
            <span className="inline-flex items-center gap-1.5"><Calendar size={13} /> Registrato {fmt(user.created_at)}</span>
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            {[["L1", user.has_access_l1], ["L2", user.has_access_l2], ["L3", user.has_access_l3], ["L4", user.has_access_l4], ["Corso Completo", user.has_access_full_course], ["Consulenza", user.consultation_booked]]
              .map(([label, val]) => (
                <span key={label} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold" style={{ background: val ? "#E0F5E9" : "#F0F4F8", color: val ? "#127c3e" : "#4B5A6B" }}>
                  {val && <CheckCircle2 size={11} />} {label}
                </span>
              ))}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <GenerateUnlockLinkButton
              userId={user.id}
              userName={user.name}
              kind="bundle_234"
              onGenerated={() => fetchData()}
            />
            <GenerateUnlockLinkButton
              userId={user.id}
              userName={user.name}
              kind="full_course"
              onGenerated={() => fetchData()}
            />
            {/* Paid-checkout magic links — routes the lead through Stripe
                instead of a free unlock (used for warm-lead recovery).
                Entrambi i pulsanti sono indipendenti: sono nascosti SOLO
                se il lead ha già quella specifica lezione (inutile mandare
                un link d'acquisto per qualcosa che possiede). L'admin è
                libero di scegliere quale mandare per primo — l'ordine
                naturale L.2 (€2) → L.3 (€10) è supportato ma non
                enforced dai pulsanti. */}
            {!user.has_access_l2 && (
              <GeneratePurchaseLinkButton
                userId={user.id}
                userName={user.name}
                lesson="lezione-2"
                onGenerated={() => fetchData()}
              />
            )}
            {!user.has_access_l3 && (
              <GeneratePurchaseLinkButton
                userId={user.id}
                userName={user.name}
                lesson="lezione-3"
                onGenerated={() => fetchData()}
              />
            )}
            <GrantBundle234Button
              userId={user.id}
              userName={user.name}
              alreadyGranted={!!user.has_access_l4}
              onGranted={() => fetchData()}
            />
            <GrantFullAccessButton
              userId={user.id}
              userName={user.name}
              alreadyGranted={!!user.has_access_full_course}
              onGranted={() => fetchData()}
            />
          </div>

          {!user.has_password && (
            <div className="mt-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
              <div className="text-sm" style={{ color: "#7a4a14" }}>
                <p className="font-semibold">⚠️ Lead senza password</p>
                <p className="text-[13px] leading-relaxed mt-0.5">
                  Questo utente non ha mai completato la Step 2. Mandagli il link per impostarla.
                </p>
              </div>
              <CopyPasswordLinkButton userId={user.id} variant="card" />
            </div>
          )}

          {user.has_password && (
            <div className="mt-5 rounded-lg border border-[#dbe3ec] bg-[#f7fafd] px-4 py-3">
              <p className="text-sm font-semibold mb-2" style={{ color: "var(--np-primary)" }}>
                🔗 Genera link di accesso rapido (auto-login, valido 48h)
              </p>
              <div className="flex flex-wrap gap-2">
                <AutoLoginLinkButton userId={user.id} to="/corso/lezione/1" label="Lezione 1" variant="table" />
                <AutoLoginLinkButton userId={user.id} to="/vendita/bundle" label="🛒 Vendita Bundle L2+L3+L4 (€50)" variant="table" />
                <AutoLoginLinkButton userId={user.id} to="/vendita/lezioni-avanzate" label="🛒 Vendita Lezioni 5-9 (€147)" variant="table" />
                <AutoLoginLinkButton userId={user.id} to="/vendita/corso-completo" label="🛒 Carrello Corso Completo (€197)" variant="table" />
                <AutoLoginLinkButton userId={user.id} to="/vendita/coaching-completo" label="🛒 Coaching Premium (€490)" variant="table" />
                <AutoLoginLinkButton userId={user.id} to="/corso" label="Area Membri" variant="table" />
                <CopyPasswordLinkButton userId={user.id} variant="table" />
              </div>
              <p className="text-[11px] mt-2 italic" style={{ color: "var(--np-muted)" }}>
                Il link logga automaticamente l&apos;utente e lo manda alla pagina scelta. Copia + incolla su WhatsApp/email.
                Il <strong>Reset password</strong> manda un link per impostare una nuova password (valido 14 giorni).
              </p>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <PurchaseMarkersPanel
            userId={user.id}
            markers={user.purchase_markers || []}
            onChanged={() => fetchData()}
          />
          <TrackedLinksPanel userId={user.id} refreshKey={user.updated_at || user.id} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-2xl border p-6" style={{ borderColor: "rgba(26,42,58,0.08)" }}>
            <h3 className="font-semibold mb-4 inline-flex items-center gap-2" style={{ color: "var(--np-primary)" }}><StickyNote size={16} /> Note interne ({notes.length})</h3>
            <div className="space-y-3 mb-4 max-h-[420px] overflow-y-auto">
              {notes.length === 0 && <p className="text-sm" style={{ color: "var(--np-muted)" }}>Nessuna nota.</p>}
              {notes.map(n => (
                <div key={n.id} className="rounded-lg p-3 text-sm" style={{ background: "var(--np-quaternary)" }}>
                  <p style={{ color: "var(--np-primary)" }}>{n.note}</p>
                  <p className="text-[11px] mt-1.5" style={{ color: "var(--np-muted)" }}>{fmt(n.created_at)}</p>
                </div>
              ))}
            </div>
            <textarea data-testid="admin-note-input" value={newNote} onChange={e => setNewNote(e.target.value)} rows={3} placeholder="Aggiungi una nota datata…" className="w-full rounded-lg border px-3 py-2 text-sm" style={{ borderColor: "#dbe3ec" }} />
            <button data-testid="admin-note-submit" onClick={addNote} disabled={!newNote.trim() || saving} className="cta-btn mt-2 disabled:opacity-50">
              {saving ? <Loader2 className="animate-spin" size={15} /> : <><Send size={14} /> Salva nota</>}
            </button>
          </div>

          <div className="bg-white rounded-2xl border p-6" style={{ borderColor: "rgba(26,42,58,0.08)" }}>
            <h3 className="font-semibold mb-4" style={{ color: "var(--np-primary)" }}>Timeline eventi ({events.length})</h3>
            <div className="space-y-3 max-h-[520px] overflow-y-auto">
              {events.length === 0 && <p className="text-sm" style={{ color: "var(--np-muted)" }}>Nessun evento.</p>}
              {events.map((e, i) => {
                const d = formatEvent(e.event_name, e.payload || {});
                const color = EVENT_GROUP_COLOR[(eventDescriptor(e.event_name).group)] || "#94a3b8";
                return (
                  <div key={`${e.created_at}-${e.event_name}-${i}`} className="border-l-2 pl-3 py-1" style={{ borderColor: color }}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold" style={{ color: "var(--np-primary)" }}>
                        <span className="mr-1">{d.icon}</span>{d.label}
                      </span>
                      <span className="text-[11px]" style={{ color: "var(--np-muted)" }}>{fmt(e.created_at)}</span>
                    </div>
                    {e.payload?.amount_cents > 0 && (
                      <p className="text-sm font-semibold mt-1" style={{ color: "var(--np-secondary)" }}>
                        💰 € {(e.payload.amount_cents / 100).toFixed(2)}
                      </p>
                    )}
                    {d.label !== e.event_name && (
                      <p className="text-[10px] font-mono mt-0.5" style={{ color: "var(--np-muted)" }}>{d.sublabel || e.event_name}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* WhatsApp scheduled messages panel — moved to the very bottom on
            user request: it's the noisiest block on the page (10+ rows
            per lead) and the admin usually needs it only for debugging,
            after checking notes & timeline first. */}
        <div>
          <UserScheduledMessagesPanel userId={user.id} />
        </div>
      </section>
    </main>
  );
}
